import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Spin, Button, Tooltip, Modal, Input, Dropdown, ConfigProvider, theme as antdTheme } from 'antd';
import Icon, {
    FullscreenOutlined,
    FullscreenExitOutlined,
    DisconnectOutlined,
    ReloadOutlined,
    SnippetsOutlined,
    CompressOutlined,
    ExpandOutlined,
    ColumnWidthOutlined,
    DragOutlined,
    MoreOutlined,
    CloseOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
} from '@ant-design/icons';
import RFB from '@novnc/novnc/lib/rfb';
import { useTranslation } from '../../i18n';
import { darkTheme } from '../../../theme/themeConfig';

// X11 keysyms. QEMU's VNC has no clipboard agent in the VM, so "paste" and the
// on-screen keyboard are typed as key events.
const XK = {
    BackSpace: 0xff08, Tab: 0xff09, Return: 0xff0d, Escape: 0xff1b, Delete: 0xffff,
    Left: 0xff51, Up: 0xff52, Right: 0xff53, Down: 0xff54,
    Shift: 0xffe1, Control: 0xffe3, Alt: 0xffe9, Super: 0xffeb, F1: 0xffbe,
};
// Characters that need Shift on a US layout (QEMU maps keysyms to US scancodes).
const SHIFTED = new Set('~!@#$%^&*()_+{}|:"<>?ABCDEFGHIJKLMNOPQRSTUVWXYZ');
const MAX_PASTE = 5000;
const MODS = {
    ctrl: { label: 'Ctrl', ks: XK.Control, code: 'ControlLeft' },
    alt: { label: 'Alt', ks: XK.Alt, code: 'AltLeft' },
    meta: { label: 'Win', ks: XK.Super, code: 'MetaLeft' },
};
// Hidden input buffer for the soft keyboard (same trick as noVNC's vnc.html):
// keep filler text before the caret so Backspace always produces an input event.
const KB_FILL = '_'.repeat(100);
const WIDE = 1000;
// Console chrome is always dark (next to a black screen), whatever the app theme.
const CONSOLE_THEME = {
    ...darkTheme,
    algorithm: antdTheme.darkAlgorithm,
    token: {
        ...darkTheme.token,
        colorBgContainer: '#1f1f1f', colorBgElevated: '#262626', colorBorder: '#3a3a3a',
        colorBorderSecondary: '#2a2a2a', colorSplit: '#2a2a2a',
    },
};

const keysymFor = (ch) => {
    if (ch === '\n') return XK.Return;
    if (ch === '\t') return XK.Tab;
    const cp = ch.codePointAt(0);
    if (cp >= 0x20 && cp <= 0xff) return cp;
    return 0x01000000 + cp; // Unicode keysym (works only if the guest layout has it)
};

const typeText = async (rfb, text, delay = 8) => {
    for (const ch of text.replace(/\r\n?/g, '\n')) {
        const ks = keysymFor(ch);
        const shift = SHIFTED.has(ch);
        if (shift) rfb.sendKey(XK.Shift, 'ShiftLeft', true);
        rfb.sendKey(ks, null, true);
        rfb.sendKey(ks, null, false);
        if (shift) rfb.sendKey(XK.Shift, 'ShiftLeft', false);
        // Small gap so the guest (and slow boot consoles) never drop keys.
        if (delay) await new Promise((r) => setTimeout(r, delay));
    }
};

const KeyboardSvg = () => (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M6 9h.01M10 9h.01M14 9h.01M18 9h.01M6 12.5h.01M10 12.5h.01M14 12.5h.01M18 12.5h.01M7.5 16h9" />
    </svg>
);
const KeyboardIcon = (props) => <Icon component={KeyboardSvg} {...props} />;

const Tip = ({ off, title, children }) => (off ? children : <Tooltip title={title} mouseEnterDelay={0.3}>{children}</Tooltip>);

const isTouchDevice = () => (typeof window !== 'undefined')
    && ((window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || 'ontouchstart' in window);

const fsElement = () => document.fullscreenElement || document.webkitFullscreenElement || null;
const fsEnabled = () => !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);

const readViewport = () => {
    const v = window.visualViewport;
    return v
        ? { top: Math.max(0, v.offsetTop), h: Math.round(v.height), w: Math.round(v.width) }
        : { top: 0, h: window.innerHeight, w: window.innerWidth };
};

/**
 * Full-viewport VNC console (noVNC).
 *
 * Rendered as an overlay above the page while mounted; unmount (onClose) to close it.
 *
 * @param {string} props.wsUrl - WebSocket URL (wss://host/vnc?port=xxx&token=xxx&ts=xxx)
 * @param {string} props.password - VNC password
 * @param {string} props.title - shown in the toolbar
 * @param {Function} props.onClose - close the console
 * @param {Function} props.onReconnect - fetch a fresh token (tokens expire after 5 min)
 */
const VncConsole = ({
    wsUrl,
    password,
    title,
    onClose,
    onConnect,
    onDisconnect,
    onError,
    onReconnect,
}) => {
    const { t } = useTranslation();
    const rootRef = useRef(null);
    const screenRef = useRef(null);
    const rfbRef = useRef(null);
    const kbRef = useRef(null);
    const kbLast = useRef(KB_FILL);
    const typeQueue = useRef(Promise.resolve());
    const [touch] = useState(isTouchDevice);
    const [vp, setVp] = useState(readViewport);
    const [status, setStatus] = useState('disconnected'); // disconnected, connecting, connected, error
    const [errorMsg, setErrorMsg] = useState('');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fit, setFit] = useState(true);
    const [clipping, setClipping] = useState(false);
    const [pan, setPan] = useState(false);
    const [canResize, setCanResize] = useState(false);
    const [remoteResize, setRemoteResize] = useState(false);
    const [mods, setMods] = useState({ ctrl: false, alt: false, meta: false });
    const [kbOn, setKbOn] = useState(false);
    const [keysOpen, setKeysOpen] = useState(false);
    const [pasteOpen, setPasteOpen] = useState(false);
    const [pasteText, setPasteText] = useState('');
    const [typing, setTyping] = useState(false);

    const wide = vp.w >= WIDE;
    const connected = status === 'connected';
    const fsOk = fsEnabled();

    // --- connection -------------------------------------------------------
    const releaseMods = useCallback(() => {
        const rfb = rfbRef.current;
        setMods((m) => {
            if (rfb) Object.keys(m).forEach((k) => m[k] && rfb.sendKey(MODS[k].ks, MODS[k].code, false));
            return { ctrl: false, alt: false, meta: false };
        });
    }, []);

    const connect = useCallback(() => {
        if (!wsUrl || !screenRef.current) return;
        setStatus('connecting');
        setErrorMsg('');
        setCanResize(false);
        try {
            const rfb = new RFB(screenRef.current, new URL(wsUrl).toString(), {
                credentials: { password: password || '' },
            });
            rfb.background = '#000';
            rfb.scaleViewport = fit;
            rfb.clipViewport = !fit && touch;
            rfb.resizeSession = remoteResize;

            rfb.addEventListener('connect', () => {
                setStatus('connected');
                onConnect?.();
                // ExtendedDesktopSize arrives with the first framebuffer updates.
                let n = 0;
                const iv = setInterval(() => {
                    n += 1;
                    if (rfbRef.current !== rfb || n > 15) { clearInterval(iv); return; }
                    if (rfb._supportsSetDesktopSize) { setCanResize(true); clearInterval(iv); }
                }, 1000);
            });
            rfb.addEventListener('disconnect', (e) => {
                if (rfbRef.current === rfb) rfbRef.current = null;
                setStatus('disconnected');
                setMods({ ctrl: false, alt: false, meta: false });
                if (e.detail.clean) {
                    onDisconnect?.();
                } else {
                    setErrorMsg(t('Connection closed unexpectedly'));
                    onError?.(t('Connection closed unexpectedly'));
                }
            });
            rfb.addEventListener('credentialsrequired', () => {
                if (password) {
                    rfb.sendCredentials({ password });
                } else {
                    setErrorMsg(t('VNC password required'));
                    setStatus('error');
                }
            });
            rfb.addEventListener('securityfailure', (e) => {
                setErrorMsg(`${t('Security failure')}: ${e.detail.reason}`);
                setStatus('error');
                onError?.(e.detail.reason);
            });
            rfb.addEventListener('clippingviewport', (e) => setClipping(!!e.detail));
            rfbRef.current = rfb;
        } catch (err) {
            setErrorMsg(err.message);
            setStatus('error');
            onError?.(err.message);
        }
    }, [wsUrl, password, fit, touch, remoteResize, onConnect, onDisconnect, onError, t]);

    const disconnect = useCallback(() => {
        releaseMods();
        if (rfbRef.current) {
            rfbRef.current.disconnect();
            rfbRef.current = null;
        }
        setStatus('disconnected');
    }, [releaseMods]);

    // A fresh token normally changes wsUrl (and the effect below reconnects); if the
    // parent hands back the same URL, connect again ourselves.
    const latestUrl = useRef(wsUrl);
    latestUrl.current = wsUrl;
    const connectRef = useRef(connect);
    connectRef.current = connect;
    const reconnect = useCallback(async () => {
        if (rfbRef.current) disconnect();
        const before = latestUrl.current;
        if (onReconnect) {
            try { await onReconnect(); } catch (e) { /* parent reports errors */ }
            setTimeout(() => {
                if (latestUrl.current === before && !rfbRef.current) connectRef.current();
            }, 300);
        } else {
            connect();
        }
    }, [onReconnect, connect, disconnect]);

    useEffect(() => {
        if (wsUrl) connect();
        return () => {
            if (rfbRef.current) {
                rfbRef.current.disconnect();
                rfbRef.current = null;
            }
        };
    }, [wsUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- view mode --------------------------------------------------------
    useEffect(() => {
        const rfb = rfbRef.current;
        if (!rfb) return;
        rfb.scaleViewport = fit;
        // Touch: clip + pan mode (no native scrollbars to drag). Mouse: native scrollbars.
        rfb.clipViewport = !fit && touch;
        if (fit) setPan(false);
    }, [fit, touch, status]);

    useEffect(() => {
        if (rfbRef.current) rfbRef.current.dragViewport = pan;
    }, [pan, status]);

    useEffect(() => {
        if (rfbRef.current) rfbRef.current.resizeSession = remoteResize && canResize;
    }, [remoteResize, canResize, status]);

    useEffect(() => {
        // While the soft keyboard is up, a tap on the screen must not steal focus from it.
        if (rfbRef.current) rfbRef.current.focusOnClick = !kbOn;
    }, [kbOn, status]);

    // --- viewport / page lock ---------------------------------------------
    useEffect(() => {
        let raf = 0;
        const update = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => setVp(readViewport()));
        };
        const vv = window.visualViewport;
        vv?.addEventListener('resize', update);
        vv?.addEventListener('scroll', update);
        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', update);
        return () => {
            cancelAnimationFrame(raf);
            vv?.removeEventListener('resize', update);
            vv?.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', update);
        };
    }, []);

    useEffect(() => {
        const { body, documentElement: html } = document;
        const prev = [body.style.overflow, html.style.overflow, html.style.overscrollBehavior];
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        html.style.overscrollBehavior = 'none';
        // iOS ignores overflow:hidden for touch scrolling; block it on the overlay itself.
        const root = rootRef.current;
        const block = (e) => {
            if (!e.target.closest?.('[data-vnc-scroll], .ant-modal-wrap, .ant-dropdown')) e.preventDefault();
        };
        root?.addEventListener('touchmove', block, { passive: false });
        return () => {
            [body.style.overflow, html.style.overflow, html.style.overscrollBehavior] = prev;
            root?.removeEventListener('touchmove', block);
            if (fsElement()) (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
        };
    }, []);

    useEffect(() => {
        const onFs = () => setIsFullscreen(!!fsElement());
        document.addEventListener('fullscreenchange', onFs);
        document.addEventListener('webkitfullscreenchange', onFs);
        return () => {
            document.removeEventListener('fullscreenchange', onFs);
            document.removeEventListener('webkitfullscreenchange', onFs);
        };
    }, []);

    const toggleFullscreen = useCallback(() => {
        const el = rootRef.current;
        if (!el) return;
        if (fsElement()) {
            (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
        } else {
            const req = el.requestFullscreen || el.webkitRequestFullscreen;
            Promise.resolve(req?.call(el)).catch(() => {});
        }
    }, []);

    // --- keys -------------------------------------------------------------
    const refocus = useCallback(() => {
        if (kbOn) kbRef.current?.focus({ preventScroll: true });
        else rfbRef.current?.focus?.({ preventScroll: true });
    }, [kbOn]);

    const sendKey = useCallback((ks, code) => {
        rfbRef.current?.sendKey(ks, code);
        refocus();
    }, [refocus]);

    const toggleMod = useCallback((k) => {
        const rfb = rfbRef.current;
        if (!rfb) return;
        setMods((m) => {
            rfb.sendKey(MODS[k].ks, MODS[k].code, !m[k]);
            return { ...m, [k]: !m[k] };
        });
        refocus();
    }, [refocus]);

    const sendCtrlAltDel = useCallback(() => {
        rfbRef.current?.sendCtrlAltDel();
        refocus();
    }, [refocus]);

    // Soft-keyboard output goes through one queue so backspaces, text and keys stay in order.
    const enqueue = useCallback((fn) => {
        typeQueue.current = typeQueue.current.then(() => {
            const rfb = rfbRef.current;
            return rfb ? fn(rfb) : null;
        }).catch(() => {});
    }, []);

    // --- soft keyboard ----------------------------------------------------
    const kbReset = () => {
        const el = kbRef.current;
        if (!el) return;
        el.value = KB_FILL;
        kbLast.current = KB_FILL;
        try { el.setSelectionRange(KB_FILL.length, KB_FILL.length); } catch (e) { /* ignore */ }
    };

    const toggleKeyboard = () => {
        const el = kbRef.current;
        if (!el) return;
        if (kbOn) {
            el.blur();
        } else {
            kbReset();
            el.focus({ preventScroll: true }); // must stay inside the tap handler for iOS
        }
    };

    const onKbInput = (e) => {
        const nv = e.target.value;
        const ov = kbLast.current;
        let i = 0;
        const min = Math.min(nv.length, ov.length);
        while (i < min && nv[i] === ov[i]) i += 1;
        const backspaces = ov.length - i;
        const added = nv.slice(i);
        if (backspaces || added) {
            enqueue((rfb) => {
                for (let b = 0; b < backspaces; b += 1) rfb.sendKey(XK.BackSpace, 'Backspace');
                return added ? typeText(rfb, added, 0) : null;
            });
        }
        kbLast.current = nv;
        if (nv.length < 20 || nv.length > 500) kbReset();
    };

    const KB_KEYS = {
        Enter: [XK.Return, 'Enter'], Tab: [XK.Tab, 'Tab'], Escape: [XK.Escape, 'Escape'],
        ArrowLeft: [XK.Left, 'ArrowLeft'], ArrowRight: [XK.Right, 'ArrowRight'],
        ArrowUp: [XK.Up, 'ArrowUp'], ArrowDown: [XK.Down, 'ArrowDown'], Delete: [XK.Delete, 'Delete'],
    };
    const onKbKeyDown = (e) => {
        const k = KB_KEYS[e.key];
        if (k) {
            e.preventDefault();
            enqueue((rfb) => rfb.sendKey(k[0], k[1]));
            return;
        }
        // Backspace with an empty buffer (rare) — still forward it.
        if (e.key === 'Backspace' && !e.target.value) {
            e.preventDefault();
            enqueue((rfb) => rfb.sendKey(XK.BackSpace, 'Backspace'));
        }
    };

    // --- paste ------------------------------------------------------------
    const openPaste = useCallback(async () => {
        if (!rfbRef.current || !connected) return;
        let text = '';
        try {
            text = (await navigator.clipboard?.readText?.()) || '';
        } catch (err) {
            // Firefox / denied permission: the user pastes into the box instead.
        }
        setPasteText(text.slice(0, MAX_PASTE));
        setPasteOpen(true);
    }, [connected]);

    const sendPaste = useCallback(async () => {
        const rfb = rfbRef.current;
        if (!rfb || !pasteText) return;
        setTyping(true);
        try {
            await typeText(rfb, pasteText.slice(0, MAX_PASTE));
            setPasteOpen(false);
        } finally {
            setTyping(false);
            rfb.focus?.();
        }
    }, [pasteText]);

    // --- UI -----------------------------------------------------------------
    const size = touch ? 'middle' : 'small';
    const btn = (key, tip, icon, onClick, extra = {}) => (
        <Tip key={key} off={touch} title={tip}>
            <Button type="text" size={size} icon={icon} onClick={onClick} aria-label={tip}
                onMouseDown={(e) => e.preventDefault()}
                disabled={extra.needConn !== false && !connected} {...extra.props}
                style={{ color: extra.active ? undefined : '#ddd', ...extra.style }}>
                {extra.label}
            </Button>
        </Tip>
    );
    const keyBtn = (key, label, tip, onClick, active) => (
        <Tip key={key} off={touch} title={tip}>
            <Button size={size} type={active ? 'primary' : 'default'} onClick={onClick} disabled={!connected}
                onMouseDown={(e) => e.preventDefault()}
                style={{ minWidth: touch ? 40 : 34, padding: '0 8px', fontSize: 12 }}>
                {label}
            </Button>
        </Tip>
    );

    const fnMenu = {
        items: Array.from({ length: 12 }, (_, i) => ({ key: String(i), label: `F${i + 1}` })),
        onClick: ({ key }) => sendKey(XK.F1 + Number(key), `F${Number(key) + 1}`),
    };

    const keys = [
        keyBtn('cad', 'Ctrl+Alt+Del', t('Send Ctrl+Alt+Del'), sendCtrlAltDel),
        keyBtn('esc', 'Esc', 'Esc', () => sendKey(XK.Escape, 'Escape')),
        keyBtn('tab', 'Tab', 'Tab', () => sendKey(XK.Tab, 'Tab')),
        ...Object.keys(MODS).map((k) => keyBtn(k, MODS[k].label, t('Hold {{key}}', { key: MODS[k].label }), () => toggleMod(k), mods[k])),
        keyBtn('left', <ArrowLeftOutlined />, '←', () => sendKey(XK.Left, 'ArrowLeft')),
        keyBtn('up', <ArrowUpOutlined />, '↑', () => sendKey(XK.Up, 'ArrowUp')),
        keyBtn('down', <ArrowDownOutlined />, '↓', () => sendKey(XK.Down, 'ArrowDown')),
        keyBtn('right', <ArrowRightOutlined />, '→', () => sendKey(XK.Right, 'ArrowRight')),
        <Dropdown key="fn" menu={fnMenu} trigger={['click']} disabled={!connected}>
            <Button size={size} disabled={!connected} onMouseDown={(e) => e.preventDefault()}
                style={{ minWidth: touch ? 40 : 34, padding: '0 8px', fontSize: 12 }}>F1–F12</Button>
        </Dropdown>,
    ];

    const statusColor = connected ? '#52c41a' : status === 'connecting' ? '#faad14' : '#8c8c8c';
    const statusText = connected ? t('Connected') : status === 'connecting' ? t('Connecting') : t('Disconnected');

    const fitBtn = btn('fit', fit ? t('Actual size') : t('Fit to window'),
        fit ? <ExpandOutlined /> : <CompressOutlined />, () => setFit((f) => !f), { needConn: false });
    const fsBtn = fsOk && btn('fs', isFullscreen ? t('Exit fullscreen') : t('Fullscreen'),
        isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />, toggleFullscreen, { needConn: false });
    const kbBtn = btn('kb', t('Keyboard'), <KeyboardIcon />, toggleKeyboard,
        { active: kbOn, props: { type: kbOn ? 'primary' : 'text' } });
    const panBtn = !fit && clipping && btn('pan', t('Drag to pan'), <DragOutlined />, () => setPan((p) => !p),
        { active: pan, props: { type: pan ? 'primary' : 'text' } });
    const connBtn = connected
        ? btn('disc', t('Disconnect'), <DisconnectOutlined />, disconnect)
        : btn('re', t('Reconnect'), <ReloadOutlined />, reconnect, { needConn: false, props: { loading: status === 'connecting' } });
    const closeBtn = btn('close', t('Close'), <CloseOutlined />, onClose, { needConn: false });

    const moreMenu = {
        items: [
            { key: 'paste', icon: <SnippetsOutlined />, label: t('Paste text'), disabled: !connected },
            canResize && { key: 'resize', icon: <ColumnWidthOutlined />, label: t('Resize remote'), disabled: !connected, className: remoteResize ? 'ant-dropdown-menu-item-selected' : undefined },
            { key: 're', icon: <ReloadOutlined />, label: t('Reconnect') },
            connected && { key: 'disc', icon: <DisconnectOutlined />, label: t('Disconnect'), danger: true },
        ].filter(Boolean),
        onClick: ({ key }) => {
            if (key === 'paste') openPaste();
            else if (key === 'resize') setRemoteResize((r) => !r);
            else if (key === 're') reconnect();
            else if (key === 'disc') disconnect();
        },
    };

    const toolbarStyle = {
        display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', minHeight: touch ? 44 : 38,
        background: '#141414', borderBottom: '1px solid #262626', color: '#ddd', touchAction: 'manipulation',
    };
    const titleNode = (
        <Tip off={touch} title={statusText}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: wide ? '0 1 auto' : 1 }}
                aria-label={statusText} data-status={status}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flex: 'none',
                    boxShadow: connected ? '0 0 6px #52c41a' : 'none' }} />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13, maxWidth: wide ? 260 : undefined }}>
                    {title || 'VNC'}
                </span>
            </div>
        </Tip>
    );

    const overlay = (
        <div
            ref={rootRef}
            className="vnc-console"
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed', left: 0, top: vp.top, width: '100%', height: vp.h, zIndex: 1000,
                display: 'flex', flexDirection: 'column', background: '#000', overscrollBehavior: 'none',
            }}
        >
            <ConfigProvider theme={CONSOLE_THEME} getPopupContainer={() => rootRef.current || document.body}>
                {/* Hidden input that brings up the native soft keyboard. */}
                <input
                    ref={kbRef}
                    type="text"
                    defaultValue={KB_FILL}
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    aria-hidden="true"
                    tabIndex={-1}
                    data-testid="vnc-kbinput"
                    onInput={onKbInput}
                    onKeyDown={onKbKeyDown}
                    onFocus={() => setKbOn(true)}
                    onBlur={() => setKbOn(false)}
                    style={{
                        position: 'absolute', left: 0, top: 0, width: 1, height: 1, opacity: 0,
                        fontSize: 16, border: 0, padding: 0, pointerEvents: 'none', zIndex: -1,
                    }}
                />

                <div style={toolbarStyle}>
                    {titleNode}
                    {wide ? (
                        <>
                            <div style={{ display: 'flex', gap: 4, marginLeft: 12, flexWrap: 'wrap' }}>{keys}</div>
                            <div style={{ flex: 1 }} />
                            {touch && kbBtn}
                            {btn('paste', t('Paste text'), <SnippetsOutlined />, openPaste)}
                            {canResize && btn('resize', t('Resize remote'), <ColumnWidthOutlined />, () => setRemoteResize((r) => !r),
                                { active: remoteResize, props: { type: remoteResize ? 'primary' : 'text' } })}
                            {panBtn}
                            {fitBtn}
                            {fsBtn}
                            {connBtn}
                            {closeBtn}
                        </>
                    ) : (
                        <>
                            {kbBtn}
                            {btn('keys', t('Special keys'), <span style={{ fontSize: 12, fontWeight: 600 }}>⌘</span>,
                                () => setKeysOpen((o) => !o),
                                { needConn: false, active: keysOpen, props: { type: keysOpen ? 'primary' : 'text' } })}
                            {panBtn}
                            {fitBtn}
                            {fsBtn}
                            <Dropdown menu={moreMenu} trigger={['click']}>
                                <Button type="text" size={size} icon={<MoreOutlined />} aria-label={t('More')} style={{ color: '#ddd' }} />
                            </Dropdown>
                            {closeBtn}
                        </>
                    )}
                </div>
                {!wide && keysOpen && (
                    <div data-vnc-scroll style={{
                        ...toolbarStyle, minHeight: 0, flexWrap: 'wrap', gap: 4, padding: '4px 6px',
                    }}>
                        {keys}
                    </div>
                )}

                <div style={{ flex: 1, position: 'relative', minHeight: 0, background: '#000' }}>
                    <div
                        ref={screenRef}
                        data-testid="vnc-screen"
                        style={{ position: 'absolute', inset: 0, touchAction: 'none', overflow: 'hidden' }}
                    />
                    {status !== 'connected' && (
                        <div style={{
                            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: 16, color: '#ddd', padding: 16,
                            textAlign: 'center', pointerEvents: 'none',
                        }}>
                            {status === 'connecting' && <Spin size="large" />}
                            {status !== 'connecting' && errorMsg && <div style={{ color: '#ff7875', maxWidth: 420 }}>{errorMsg}</div>}
                            {status !== 'connecting' && (
                                <Button type="primary" icon={<ReloadOutlined />} onClick={reconnect} style={{ pointerEvents: 'auto' }}>
                                    {errorMsg ? t('Retry') : t('Connect')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                <Modal
                    title={t('Paste into VM')}
                    open={pasteOpen}
                    onCancel={() => !typing && setPasteOpen(false)}
                    onOk={sendPaste}
                    okText={t('Input')}
                    confirmLoading={typing}
                    okButtonProps={{ disabled: !pasteText }}
                    getContainer={() => rootRef.current || document.body}
                    zIndex={1010}
                    destroyOnClose
                >
                    <Input.TextArea
                        value={pasteText}
                        onChange={(e) => setPasteText(e.target.value)}
                        autoSize={{ minRows: 4, maxRows: 10 }}
                        maxLength={MAX_PASTE}
                        showCount
                        placeholder={t('Paste here; it will be typed into the VM as keystrokes')}
                        autoFocus
                    />
                </Modal>
            </ConfigProvider>
        </div>
    );

    return createPortal(overlay, document.body);
};

export default VncConsole;
